import React, {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {requestPassword} from '../redux/AuthCRUD'

const initialValues = {
  email: '',
}

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email format')
    .min(3, 'Minimum 3 characters')
    .max(255, 'Maximum 255 characters')
    .required('Email is required'),
})

export function ForgotPassword() {
  const [loading, setLoading] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [isError, setIsError] = useState(false)

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, {setSubmitting, resetForm}) => {
      setLoading(true)
      setStatusMessage(null)
      setIsError(false)

      requestPassword(values.email)
        .then(() => {
          setStatusMessage(
            'If the email address exists in WebCert, a reset link has been sent. The link will expire in 24 hours.'
          )
          setIsError(false)
          resetForm()
        })
        .catch(({response}) => {
          const message = response?.data?.message ?? 'Unable to process the request. Please try again.'
          setStatusMessage(message)
          setIsError(true)
        })
        .finally(() => {
          setLoading(false)
          setSubmitting(false)
        })
    },
  })

  return (
    <form
      className='form w-100'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-9'>
        <div className='text-gray-600 fw-semibold mb-6'>Project Cost Management System</div>
        <div className='text-dark fw-bold mb-4'>Forgot Password</div>
        <p className='text-gray-600 mb-0'>
          A Password Reset email will be sent to the address provided for instructions in resetting your password.
        </p>
      </div>

      {statusMessage && (
        <div className={`mb-lg-10 alert ${isError ? 'alert-danger' : 'alert-success'}`}>
          <div className='alert-text fw-semibold'>{statusMessage}</div>
        </div>
      )}

      <div className='fv-row mb-8'>
        <label className='form-label fw-bold text-dark fs-6'>
          Email<span className='text-danger ms-1'>*</span>
        </label>
        <input
          type='email'
          placeholder='Enter your registered email'
          autoComplete='off'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control form-control-lg form-control-solid',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.email}</span>
          </div>
        )}
      </div>

      <div className='d-grid'>
        <button
          type='submit'
          id='kt_password_reset_submit'
          className='btn btn-dark py-3'
          disabled={formik.isSubmitting || !formik.isValid || loading}
        >
          {!loading && <span className='indicator-label'>Send Password Email Link</span>}
          {loading && (
            <span className='indicator-progress'>
              Sending...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>

      <div className='border-bottom border-gray-300 my-8'></div>

      <div className='d-flex flex-column gap-3'>
        <Link to='/auth/login' className='text-primary fw-semibold'>
          &larr; Back to Log In Page
        </Link>
        <a href='mailto:support@webcert.com' className='text-gray-600 text-decoration-underline fw-semibold'>
          Need Help?
        </a>
      </div>
    </form>
  )
}
